// =========================
// TOP BAR COMPONENT
// =========================
// External Libraries
import { LitElement, html, css } from 'lit';

// App Modules
import Auth from '../Auth';
import App from '../App';
import Utils from '../Utils';
import { gotoRoute } from '../Router';

// This is the PopinTopBar class that handles the top navigation bar
// It includes methods for fetching notifications, handling user interactions,
class PopinTopBar extends LitElement {
  static get properties() {
    return {
      user: { type: Object },
      notifications: { type: Array },
      unreadCount: { type: Number },
      showNotifPopup: { type: Boolean },
    };
  }

  constructor() {
    super();
    this.user = Auth.currentUser;
    this.notifications = [];
    this.unreadCount = 0;
    this.showNotifPopup = false;
  }

  // LIFECYCLE METHODS

  // This method is called when the component is first updated
  async firstUpdated() {
    this.user = Auth.currentUser;
    await this.fetchNotifications();
    const notifPopup = this.shadowRoot?.getElementById('notifPopup');
    notifPopup?.addEventListener('pointerleave', () => {
      const focusedItem = this.shadowRoot?.querySelector('sl-menu-item:focus');
      focusedItem?.blur();
    });
  }

  // DATA FETCHING

  // This method fetches notifications from the server
  async fetchNotifications() {
    try {
      const res = await fetch(`${App.apiBase}/notification`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        this.notifications = data;
        this.unreadCount = data.filter((n) => !n.isRead).length;
      }
    } catch (err) {
      console.warn('Failed to fetch notifications:', err);
    }
  }

  // EVENT HANDLERS

  // This method handles the click event on a notification
  // It marks the notification as read and navigates to the linked page
  async handleNotificationClick(notification) {
    if (!notification.isRead) {
      try {
        await fetch(`${App.apiBase}/notification/${notification._id}`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });
        notification.isRead = true;
        this.unreadCount -= 1;
      } catch (err) {
        console.warn('Error marking notification as read:', err);
      }
    }

    if (notification.link) {
      gotoRoute(notification.link);
    }

    this.requestUpdate();
  }

  // This method handles the click event on the profile icon
  togglePopup() {
    const popup = this.shadowRoot.getElementById('profilePopup');
    popup.active = !popup.active;
  }

  // This method handles the click event on the notification icon
  toggleNotifPopup() {
    const popup = this.shadowRoot?.getElementById('notifPopup');

    if (window.innerWidth <= 768) {
      popup.placement = 'bottom';
    } else {
      popup.placement = 'bottom-end';
    }

    popup.active = !popup.active;
  }

  // UTILITY FUNCTIONS

  // This method returns the icon name based on the notification type
  getIconNameForType(type) {
    switch (type) {
      case 'like':
        return 'hand-thumbs-up-fill';
      case 'comment':
        return 'chat-left-dots-fill';
      case 'follow':
        return 'person-plus-fill';
      case 'message':
        return 'envelope-fill';
      case 'event':
        return 'calendar-event-fill';
      default:
        return 'bell-fill';
    }
  }

  render() {
    const displayName = Utils.getDisplayName(this.user);
    const profileImage = Utils.getProfileImage(this.user);

    return html`
      <div class="top-bar">
        <div class="search">
          <sl-input
            placeholder="Search..."
            clearable
            type="text"
            size="medium"
          ></sl-input>
        </div>
        <div class="right">
          <div id="notif-anchor" @click=${this.toggleNotifPopup}>
            <sl-icon name="bell-fill"></sl-icon>
            ${this.unreadCount > 0
              ? html`<span class="notif-badge">${this.unreadCount}</span>`
              : ''}
          </div>

          <sl-popup
            id="notifPopup"
            class="notif-popup"
            anchor="notif-anchor"
            placement="bottom-end"
            arrow
          >
            ${this.notifications.length === 0
              ? html`<p class="notif-empty">No notifications</p>`
              : html`
                  <sl-menu>
                    <div class="notif-header">Unread</div>
                    ${this.notifications.filter((n) => !n.isRead).length === 0
                      ? html`<p class="notif-empty">No unread notifications</p>`
                      : this.notifications
                          .filter((n) => !n.isRead)
                          .map(
                            (notif) => html`
                              <sl-menu-item
                                @click=${() =>
                                  this.handleNotificationClick(notif)}
                              >
                                <sl-icon
                                  class="unread"
                                  slot="prefix"
                                  name=${this.getIconNameForType(notif.type)}
                                ></sl-icon>
                                <div class="notif-content unread">
                                  <p>${notif.content}</p>
                                  <span class="notif-time"
                                    >${Utils.formatTime(notif.timestamp)}</span
                                  >
                                </div>
                              </sl-menu-item>
                            `
                          )}

                    <div class="notif-header">Read</div>
                    ${this.notifications.filter((n) => n.isRead).length === 0
                      ? html`<p class="notif-empty">No read notifications</p>`
                      : this.notifications
                          .filter((n) => n.isRead)
                          .map(
                            (notif) => html`
                              <sl-menu-item
                                @click=${() =>
                                  this.handleNotificationClick(notif)}
                              >
                                <sl-icon
                                  slot="prefix"
                                  name=${this.getIconNameForType(notif.type)}
                                ></sl-icon>
                                <div class="notif-content">
                                  <p>${notif.content}</p>
                                  <span class="notif-time"
                                    >${Utils.formatTime(notif.timestamp)}</span
                                  >
                                </div>
                              </sl-menu-item>
                            `
                          )}
                  </sl-menu>
                `}
          </sl-popup>

          <div id="profile-anchor" @click=${this.togglePopup}>
            <img class="pfp-icon" src=${profileImage} />
            <span class="user-name">${displayName}</span>
          </div>
          <sl-popup
            id="profilePopup"
            class="profile-popup"
            anchor="profile-anchor"
            placement="bottom-end"
            arrow
          >
            <sl-menu>
              <sl-menu-item
                @click=${() => gotoRoute(`/profile?user=${this.user._id}`)}
              >
                <sl-icon slot="prefix" name="person-fill"></sl-icon>
                View Profile
              </sl-menu-item>
              <sl-menu-item @click=${() => gotoRoute('/editProfile')}>
                <sl-icon slot="prefix" name="pencil-fill"></sl-icon>
                Edit Profile
              </sl-menu-item>
              <sl-menu-item @click=${() => gotoRoute('/settings')}>
                <sl-icon slot="prefix" name="gear-fill"></sl-icon>
                Settings
              </sl-menu-item>
              <hr />
              <sl-menu-item @click=${() => Auth.signOut()}>
                <sl-icon slot="prefix" name="box-arrow-right"></sl-icon>
                Sign Out
              </sl-menu-item>
              <sl-menu-item
                @click=${() => {
                  const popup = this.shadowRoot.getElementById('profilePopup');
                  popup.active = false;

                  const onFeedPage = window.location.pathname === '/feed';

                  if (onFeedPage) {
                    document.querySelector('popin-guide')?.startGuide();
                  } else {
                    localStorage.setItem('startGuideOnLoad', 'true');
                    gotoRoute('/feed');
                  }
                }}
              >
                <sl-icon slot="prefix" name="info-circle-fill"></sl-icon>
                Guide
              </sl-menu-item>
            </sl-menu>
          </sl-popup>
        </div>
      </div>
    `;
  }

  static styles = css`
    .top-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1rem 2rem;
      background-color: white;
    }

    .search {
      width: 50%;
    }

    .right {
      display: flex;
      align-items: center;
      gap: 2rem;
    }

    .right div {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding-right: 1.5rem;
    }

    .user-name {
      font-weight: 600;
      color: var(--base-txt-color);
    }

    .pfp-icon {
      width: 30px;
      height: 30px;
      object-fit: cover;
      border-radius: 100px;
    }

    #profile-anchor {
      cursor: pointer;
    }

    .profile-popup {
      z-index: 1000;
    }

    .profile-popup::part(popup) {
      margin: 20px 0px;
      border-radius: 12px;
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
      background-image: linear-gradient(
        var(--sl-color-primary-600),
        var(--sl-color-primary-500)
      );
      border: none;
    }

    .profile-popup::part(arrow) {
      background-color: var(--sl-color-primary-600);
    }

    .profile-popup sl-button {
      margin-top: 20px;
      padding: 0px;
      width: 100%;
    }

    sl-input::part(base) {
      background-color: var(--sl-color-primary-100);
      border: none;
    }

    sl-menu {
      background-color: transparent;
      border: none;
    }

    sl-menu-item::part(base):hover,
    sl-menu-item::part(base):focus {
      background-color: var(--sl-color-primary-700);
    }

    sl-popup sl-menu sl-menu-item::part(prefix),
    sl-popup sl-menu sl-menu-item::part(label) {
      font-family: var(--heading-font-family);
      color: var(--white);
    }

    .notif-content {
      display: flex;
      justify-content: space-between;
      gap: 2rem !important;
      padding-right: 0px !important;
    }

    #notif-anchor {
      position: relative;
      padding: 0px;
      display: flex;
      justify-content: flex-end;
      align-items: flex-start;
    }

    #notif-anchor sl-icon {
      font-size: 1.6rem;
      color: var(--sl-color-primary-300);
    }

    .notif-badge {
      position: absolute;
      top: -4px;
      right: -4px;
      display: flex;
      justify-content: center;
      align-items: center;
      background: red;
      color: white;
      font-size: 0.6rem;
      border-radius: 50%;
      width: 15px;
      height: 15px;
      font-weight: bold;
      text-align: center;
    }

    .notif-popup {
      z-index: 1000;
    }

    .notif-popup::part(popup) {
      margin: 20px 0px;
      border-radius: 12px;
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
      background-color: var(--white);
      border: none;
    }

    .notif-popup::part(arrow) {
      background-color: var(--white);
    }

    .notif-empty {
      padding: 1rem;
      font-size: 0.9rem;
      color: gray;
    }

    .notif-header {
      padding: 0.5rem 1rem;
      font-weight: bold;
      border-bottom: solid var(--standard-border) 1px;
    }

    .notif-popup sl-menu sl-menu-item::part(base) {
      border-bottom: solid var(--standard-border) 1px;
      margin: 0px 15px;
    }

    .notif-popup sl-menu sl-menu-item::part(base):hover,
    .notif-popup sl-menu sl-menu-item::part(base):focus-visible {
      background-color: var(--sl-color-primary-100);
    }

    .notif-popup sl-menu sl-menu-item::part(label),
    .notif-popup sl-menu sl-menu-item::part(prefix),
    .notif-popup sl-menu sl-menu-item::part(suffix) {
      color: black;
    }

    .notif-popup sl-menu sl-menu-item .notif-content.unread,
    .notif-popup sl-menu sl-menu-item sl-icon.unread {
      color: var(--sl-color-primary-500);
      font-weight: bold;
    }

    .notif-popup sl-menu sl-menu-item::part(prefix) {
      margin-inline-end: 10px;
    }

    @media all and (max-width: 1366px) {
    }

    @media all and (max-width: 1024px) {
    }

    @media all and (max-width: 768px) {
      :host {
        margin-left: 50px;
      }

      .top-bar {
        flex-direction: column;
        padding: 0.75rem !important;

        .search {
          order: 2;
          width: 100%;
        }
        .right {
          width: 100%;
          padding-bottom: 20px;
          justify-content: flex-end;
        }
      }

      .notif-popup sl-menu sl-menu-item .notif-content {
        width: 100%;
        max-width: 240px;
        gap: 1rem !important;
        overflow: hidden;
      }

      .notif-popup sl-menu sl-menu-item .notif-content p {
        width: 100%;
        margin: 0;
        overflow-wrap: break-word;
        word-break: break-word;
        white-space: normal;
        font-size: 0.9rem;
      }
    }

    @media all and (max-width: 576px) {
    }
  `;
}

customElements.define('popin-top-bar', PopinTopBar);
